// InspirationResults.jsx - Version complète avec navigation harmonisée
import React, { useState } from 'react';
import { 
  CircularProgress,
  Chip,
  Button,
  Alert
} from '@mui/material';
import { 
  Globe, 
  CheckCircle, 
  Zap,
  AlertTriangle,
  ExternalLink,
  Target,
  Filter,
  Star,
  Shield,
  Trash2,
  Plus
} from 'lucide-react';

// Import du service géographique pour les noms de langues
import GeoLanguageService from '../components/GeoLanguageService';
// Import du composant de confirmation de suppression
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const InspirationResults = ({ 
  sources,
  selectedSources,
  setSelectedSources,
  metadata,
  onBackToSearch,
  onBackToSynthesis,
  onGenerateSynthesis,
  generating,
  onDeleteSource,
  onAddSource
}) => {
  // ============================================================================
  // ÉTATS POUR LA CONFIRMATION DE SUPPRESSION
  // ============================================================================
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    source: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleSourceToggle = (source) => {
    setSelectedSources(prev => {
      const isSelected = prev.some(s => s.domain === source.domain);
      if (isSelected) {
        return prev.filter(s => s.domain !== source.domain);
      } else {
        return [...prev, source];
      }
    });
  };

  // Handler pour ouvrir le modal de confirmation de suppression
  const handleDeleteClick = (source, e) => {
    e.stopPropagation();
    setDeleteConfirmation({
      isOpen: true,
      source: source
    });
  };

  // Handler pour confirmer la suppression
  const handleConfirmDelete = async () => {
    if (!deleteConfirmation.source || !onDeleteSource) return;
    
    setIsDeleting(true);
    try {
      await onDeleteSource(deleteConfirmation.source);
      setDeleteConfirmation({ isOpen: false, source: null });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handler pour fermer le modal
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setDeleteConfirmation({ isOpen: false, source: null });
    }
  };

  const handleSelectAll = () => {
    setSelectedSources(sources);
  };

  const handleDeselectAll = () => {
    setSelectedSources([]);
  };

  const handleSelectByScore = (minScore) => {
    const highScoreSources = sources.filter(source => 
      (source.inspirationScore || 5) >= minScore
    );
    setSelectedSources(highScoreSources);
  };

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================
  const getSourceTypeColor = (type) => {
    const colors = {
      'Educational': 'bg-blue-100 text-blue-800',
      'Media': 'bg-orange-100 text-orange-800',
      'Non-profit': 'bg-green-100 text-green-800',
      'Commercial': 'bg-purple-100 text-purple-800',
      'Governmental': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'bg-emerald-400';
    if (score >= 6) return 'bg-amber-400';
    if (score >= 4) return 'bg-orange-400';
    return 'bg-rose-400';
  };

  const getReliabilityConfig = (level) => {
    const configs = {
      high: { badge: 'bg-green-100 text-green-800 border-green-300', text: 'High' },
      medium: { badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', text: 'Medium' },
      low: { badge: 'bg-red-100 text-red-800 border-red-300', text: 'Low' }
    };
    return configs[level?.toLowerCase()] || configs.medium;
  };

  const getProviderIcon = (provider) => {
    const icons = {
      'Google': '🔍',
      'OpenAI': '🧠',
      'Open AI': '🧠',
      'DuckDuckGo': '🦆',
      'Aladinn': '🚀'
    };
    return icons[provider] || '📡';
  };

  const getProvidersText = () => {
    if (!metadata || !metadata.providers) return 'recherche web';
    if (!Array.isArray(metadata.providers)) return 'recherche web';
    if (metadata.providers.length === 0) return 'recherche web';
    return metadata.providers.join(', ');
  };

  // ============================================================================
  // COMPOSANTS DE SCORES
  // ============================================================================
  const ScoreBar = ({ score, label, icon }) => {
    const percentage = (score / 10) * 100;
    const colorClass = getScoreColor(score);
    
    // Déterminer la couleur de fond du badge selon le score (couleurs plus douces)
    const getBadgeColor = (score) => {
      if (score >= 8) return 'bg-emerald-100 text-emerald-800';
      if (score >= 6) return 'bg-amber-100 text-amber-800';
      if (score >= 4) return 'bg-orange-100 text-orange-800';
      return 'bg-rose-100 text-rose-800';
    };
    
    return (
      <div className="w-24 sm:w-28 flex-shrink-0">
        <div className="flex items-center gap-1 mb-1">
          <span className="flex-shrink-0">{icon}</span>
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">{label}</span>
          <span className={`hidden sm:inline px-1.5 py-0.5 text-xs font-semibold rounded whitespace-nowrap ${getBadgeColor(score)}`}>
            {score}/10
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${colorClass} transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  const ReliabilityBar = ({ level }) => {
    if (!level) return null;
    
    const config = {
      high: { percentage: 100, color: 'bg-emerald-400', text: 'High', badge: 'bg-emerald-100 text-emerald-800' },
      specialized: { percentage: 90, color: 'bg-blue-400', text: 'Specialized', badge: 'bg-blue-100 text-blue-800' },
      medium: { percentage: 60, color: 'bg-amber-400', text: 'Medium', badge: 'bg-amber-100 text-amber-800' },
      low: { percentage: 30, color: 'bg-rose-400', text: 'Low', badge: 'bg-rose-100 text-rose-800' }
    };
    
    const reliabilityConfig = config[level.toLowerCase()] || config.medium;
    
    return (
      <div className="w-24 sm:w-28 flex-shrink-0">
        <div className="flex items-center gap-1 mb-1">
          <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">Fiabilité</span>
          <span className={`hidden sm:inline px-1.5 py-0.5 text-xs font-semibold rounded whitespace-nowrap ${reliabilityConfig.badge}`}>
            {reliabilityConfig.text}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${reliabilityConfig.color} transition-all duration-300`}
            style={{ width: `${reliabilityConfig.percentage}%` }}
          />
        </div>
      </div>
    );
  };

  // ============================================================================
  // COMPOSANTS
  // ============================================================================
  const ResultsHeader = () => (
    <div className="space-y-4">
      {/* Titre et description */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <Globe className="h-6 w-6 mr-2 text-purple-600" />
          Sources d'inspiration trouvées
        </h3>
        <p className="text-gray-600 mt-1">
          {sources.length} sources découvertes via {getProvidersText()}
        </p>
        {metadata.recovery && metadata.recovery.totalFallbacks > 0 && (
          <p className="text-orange-600 text-sm mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {metadata.recovery.totalFallbacks} sources récupérées automatiquement
          </p>
        )}
      </div>
      
      {/* Boutons - Version responsive */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button
          onClick={onBackToSearch}
          variant="outlined"
          className="text-gray-600 border-gray-300 hover:bg-gray-50 w-full sm:w-auto"
          sx={{ textTransform: 'none' }}
        >
          Modifier la recherche
        </Button>
        {onBackToSynthesis && (
          <Button
            onClick={onBackToSynthesis}
            variant="outlined"
            className="text-green-600 border-green-300 hover:bg-green-50 w-full sm:w-auto"
            sx={{ textTransform: 'none' }}
          >
            Retour à la synthèse
          </Button>
        )}
        <Button
          onClick={onGenerateSynthesis}
          disabled={generating || selectedSources.length === 0}
          variant="contained"
          className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          startIcon={generating ? <CircularProgress size={16} /> : <Zap />}
          sx={{ textTransform: 'none' }}
        >
          {generating ? 'Génération...' : 'Créer la synthèse'}
        </Button>
      </div>
    </div>
  );
 
  const SourceCard = ({ source, index }) => {
    const isSelected = selectedSources.some(s => s.domain === source.domain);
    
    return (
      <div
        key={index}
        className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md max-w-full overflow-hidden ${
          isSelected
            ? 'border-purple-300 bg-purple-50 ring-2 ring-purple-200'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => handleSourceToggle(source)}
      >
        <div className="flex items-start justify-between min-w-0">
          <div className="flex-1 min-w-0">
            {/* En-tête avec provider */}
            <div className="flex items-center justify-between mb-2 gap-2">
              <h4 className="font-medium text-gray-800 flex-1 min-w-0 break-words">
                {source.title || source.domain}
              </h4>
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* Afficher le provider avec icône */}
                {source.provider && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium flex items-center whitespace-nowrap">
                    <span className="mr-1">{getProviderIcon(source.provider)}</span>
                    {source.provider}
                  </span>
                )}
                {/* Bouton de suppression */}
                {onDeleteSource && (
                  <button
                    onClick={(e) => handleDeleteClick(source, e)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    title="Supprimer cette source"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {isSelected && (
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                )}
              </div>
            </div>
            
            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Chip 
                label={source.type || 'Web'} 
                size="small" 
                className={`${getSourceTypeColor(source.type)} font-medium`}
              />
            </div>
            
            {/* Description */}
            <p className="text-gray-600 text-sm mb-3 leading-relaxed">
              {source.description || `Source d'inspiration découverte sur ${source.domain}`}
            </p>
            
            {/* Barres de scores (Pertinence, Fiabilité, Popularité) */}
            {((source.relevanceScore ?? source.inspirationScore) !== undefined || 
              (source.reliability) || 
              (source.popularityScore ?? source.popularity) !== undefined) && (
              <div className="flex flex-nowrap gap-2 sm:gap-4 mb-3 overflow-x-auto pb-2">
                {/* Pertinence */}
                {(source.relevanceScore ?? source.inspirationScore) !== undefined && (
                  <ScoreBar 
                    score={source.relevanceScore ?? source.inspirationScore}
                    label="Pertinence"
                    icon={<Target className="h-4 w-4 text-purple-600" />}
                  />
                )}
                
                {/* Fiabilité */}
                {source.reliability && (
                  <ReliabilityBar level={source.reliability} />
                )}
                
                {/* Popularité */}
                {(source.popularityScore ?? source.popularity) !== undefined && (
                  <ScoreBar 
                    score={source.popularityScore ?? source.popularity}
                    label="Popularité"
                    icon={<Star className="h-4 w-4 text-yellow-600" />}
                  />
                )}
              </div>
            )}
            
            {/* Métadonnées avec provider en évidence */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0">
                <span className="flex items-center whitespace-nowrap">
                  <Globe className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{source.domain}</span>
                </span>
                
                {/* Provider avec style différent dans les métadonnées */}
                {source.provider && (
                  <span className="flex items-center text-blue-600 whitespace-nowrap">
                    <span className="mr-1">{getProviderIcon(source.provider)}</span>
                    <span className="font-medium">{source.provider}</span>
                  </span>
                )}
                
                {source.language && (
                  <span className="uppercase font-medium whitespace-nowrap">
                    {GeoLanguageService.getLanguageInfo(source.language)?.name || source.language}
                  </span>
                )}
                
                {source.hasUrlFallback && (
                  <span className="text-orange-600 flex items-center whitespace-nowrap">
                    <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span>URL récupérée</span>
                  </span>
                )}
              </div>
              
              {/* Lien externe */}
              {(source.usableUrl || source.url) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(source.usableUrl || source.url || `https://${source.domain}`, '_blank');
                  }}
                  className="text-blue-600 hover:text-blue-800 flex items-center whitespace-nowrap flex-shrink-0"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Visiter
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Indicateur de sélection visuel */}
        {isSelected && (
          <div className="mt-3 pt-3 border-t border-purple-200">
            <div className="text-purple-700 text-sm font-medium flex items-center">
              <CheckCircle className="h-4 w-4 mr-2" />
              Sélectionnée pour la synthèse
            </div>
          </div>
        )}
      </div>
    );
  };

  const SelectionSummary = () => {
    if (selectedSources.length === 0) return null;

    // Calculer la distribution des providers
    const providerDistribution = selectedSources.reduce((acc, source) => {
      const provider = source.provider || 'Inconnu';
      acc[provider] = (acc[provider] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-800 font-medium">
              {selectedSources.length} source(s) sélectionnée(s) pour la synthèse
            </p>
            {/* Affichage distribution providers */}
            <div className="mt-2">
              <span className="text-sm text-green-700">
                Providers: {Object.entries(providerDistribution).map(([provider, count]) => 
                  `${getProviderIcon(provider)} ${provider} (${count})`
                ).join(', ')}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EmptyState = () => (
    <div className="text-center py-12">
      <Globe className="h-16 w-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-700 mb-2">
        Aucune source trouvée
      </h3>
      <p className="text-gray-500 mb-4">
        Essayez de modifier vos paramètres de recherche ou votre requête.
      </p>
      <Button
        onClick={onBackToSearch}
        variant="contained"
        className="bg-purple-600 hover:bg-purple-700"
      >
        Modifier la recherche
      </Button>
    </div>
  );

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================
  if (sources.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <ResultsHeader />

      {/* Résumé de sélection */}
      <SelectionSummary />

      {/* Alertes */}
      {selectedSources.length === 0 && (
        <Alert severity="info">
          Sélectionnez au moins une source pour générer votre synthèse d'inspiration.
        </Alert>
      )}

      {generating && (
        <Alert severity="info" className="bg-blue-50 border-blue-200">
          <div className="flex items-center">
            <CircularProgress size={20} className="mr-3 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">Génération de votre synthèse en cours...</p>
              <p className="text-blue-600 text-sm">
                Analyse de {selectedSources.length} sources et sauvegarde automatique
              </p>
            </div>
          </div>
        </Alert>
      )}

      {/* Liste des sources */}
      <div className="grid gap-4">
        {sources.map((source, index) => (
          <SourceCard key={`${source.domain}-${index}`} source={source} index={index} />
        ))}
      </div>

      {/* Métadonnées de recherche */}
      {metadata.searchQuery && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-800 mb-3 flex items-center">
            <Globe className="h-4 w-4 mr-2" />
            Détails de la recherche
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-700">
                <span className="font-medium">Requête :</span> "{metadata.searchQuery}"
              </p>
              <p className="text-gray-600 mt-1">
                <span className="font-medium">Sources :</span> {getProvidersText()}
              </p>
            </div>
            <div>
              <p className="text-gray-600">
                <span className="font-medium">Total trouvées :</span> {metadata.count || sources.length}
              </p>
              {metadata.recovery && (
                <p className="text-orange-600 mt-1">
                  <span className="font-medium">Récupérées :</span> {metadata.recovery.totalFallbacks}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions finales - Version responsive */}
      <div className="flex justify-center pt-4">
        <Button
          onClick={onGenerateSynthesis}
          disabled={generating || selectedSources.length === 0}
          variant="contained"
          size="large"
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 px-4 sm:px-8 py-3 w-full sm:w-auto"
          startIcon={generating ? <CircularProgress size={20} /> : <Zap />}
          sx={{ textTransform: 'none' }}
        >
          {generating ? (
            <span className="hidden sm:inline">Génération et sauvegarde automatique...</span>
          ) : (
            <>
              <span className="sm:hidden">Créer synthèse ({selectedSources.length})</span>
              <span className="hidden sm:inline">Créer la synthèse ({selectedSources.length} sources)</span>
            </>
          )}
        </Button>
      </div>

      {/* Modal de confirmation de suppression */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={deleteConfirmation.source?.title || deleteConfirmation.source?.domain || "cette source"}
        isDeleting={isDeleting}
        itemType="source"
      />
    </div>
  );
}; 

export default InspirationResults;
